import styled, { css } from 'styled-components'

/**
 * Styled-components for the redesigned sidebar nav.
 * Visual reference: sidebar.html (lime active fill, ink hover, mono labels).
 */

export const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  margin: 0;
`

export const NavItemRoot = styled.li<{ $hasSubActive?: boolean; $collapsed?: boolean }>`
  position: relative;
  list-style: none;
`

export const NavLink = styled.a<{
  $active?: boolean
  $collapsed?: boolean
  $hasSubActive?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.ink};
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accent};
      color: ${theme.colors.ink};

      &:hover {
        background: ${theme.colors.accentDeep};
      }
    `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding: 0;
      width: 44px;
      height: 44px;
      margin: 0 auto;
      border-radius: 12px;
      position: relative;
    `}

  ${({ $collapsed, $hasSubActive, theme }) =>
    $collapsed &&
    $hasSubActive &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 6px;
        inset-inline-end: 6px;
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: ${theme.colors.accent};
        border: 1.5px solid ${theme.colors.surface};
      }
    `}
`

export const Ico = styled.span<{ $active?: boolean }>`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ink : theme.colors.icon};

  svg { width: 18px; height: 18px; }
`

export const Lbl = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const Badge = styled.span<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.ink : theme.colors.bg};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.ink2};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.ink : theme.colors.inputBorder};
`

export const Chev = styled.span<{ $open?: boolean }>`
  display: grid;
  place-items: center;
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.ink2};
  flex-shrink: 0;
  transition: transform 0.18s ease;
  transform: ${({ $open }) => ($open ? 'rotate(-90deg)' : 'rotate(90deg)')};

  svg { width: 14px; height: 14px; }
`

export const Submenu = styled.ul<{ $open: boolean }>`
  list-style: none;
  margin: 2px 0 0;
  padding-inline-start: 22px;
  position: relative;
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '320px' : '0')};
  transition: max-height 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    bottom: 4px;
    inset-inline-start: 22px;
    width: 1px;
    background: ${({ theme }) => theme.colors.ink4};
  }
`

export const SubmenuItem = styled.li`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 50%;
    width: 10px;
    height: 1px;
    background: ${({ theme }) => theme.colors.ink4};
  }
`

export const SubmenuLink = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  margin-inline-start: 14px;
  border-radius: 8px;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.ink};
      color: #fff;
      font-weight: 500;
    `}
`

/* Hover-flyout for collapsed mode */
export const Flyout = styled.div<{ $tip?: boolean }>`
  position: absolute;
  inset-inline-start: calc(100% + 10px);
  top: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 12px;
  padding: ${({ $tip }) => ($tip ? '6px 10px' : '6px')};
  min-width: ${({ $tip }) => ($tip ? '0' : '180px')};
  box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.12);
  opacity: 0;
  transform: translateX(-4px);
  pointer-events: none;
  transition:
    opacity ${({ theme }) => theme.motion.fast},
    transform ${({ theme }) => theme.motion.fast};
  z-index: 40;
  white-space: nowrap;
  direction: rtl;

  ${NavItemRoot}:hover > & {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
`

export const FlyoutTitle = styled.div`
  padding: 8px 10px 6px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line2};
  margin-bottom: 4px;
`

export const FlyoutLink = styled.a<{ $active?: boolean }>`
  display: block;
  padding: 7px 10px;
  border-radius: 8px;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 12.5px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink2};
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.ink};
      color: #fff;
      font-weight: 500;
    `}
`
